import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, money, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { StockQtyCell, isLowStock } from "@/components/StockQty";
import { LineItemsEditor } from "@/components/LineItemsEditor";
import { toast } from "sonner";
import { Plus, Search, QrCode, Loader2, Repeat, FileText, Briefcase, Trash2, Download } from "lucide-react";

export function RecordForm({ fields, value, onChange }) {
  const [lookups, setLookups] = useState({});

  useEffect(() => {
    fields.filter((f) => f.type === "lookup").forEach((f) => {
      api.get(`/${f.entity}`).then((r) => setLookups((p) => ({ ...p, [f.key]: r.data }))).catch(() => {});
    });
  }, [fields]);

  const set = (k, v, mirror, mirrorVal, extra = {}) => {
    const next = { ...value, [k]: v, ...extra };
    if (mirror) next[mirror] = mirrorVal;
    // Clear dependent lookups that no longer match the new parent value
    fields.forEach((f2) => {
      const dep = f2.dependsOn;
      if (dep && dep.parent === k && next[f2.key] && v) {
        const opt = (lookups[f2.key] || []).find((o) => o.id === next[f2.key]);
        if (!opt || opt[dep.attr] !== v) {
          next[f2.key] = "";
          if (f2.mirror) next[f2.mirror] = "";
        }
      }
    });
    onChange(next);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {fields.map((f) => {
        const full = f.type === "textarea" || f.type === "lineitems";
        return (
          <div key={f.key} className={full ? "md:col-span-2" : ""}>
            <Label className="text-xs text-slate-600">{f.label}{f.required && " *"}</Label>
            {f.type === "textarea" ? (
              <Textarea data-testid={`field-${f.key}`} value={value[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="mt-1" rows={2} />
            ) : f.type === "boolean" ? (
              <div className="mt-2"><Switch data-testid={`field-${f.key}`} checked={!!value[f.key]} onCheckedChange={(v) => set(f.key, v)} /></div>
            ) : f.type === "lineitems" ? (
              <div className="mt-1"><LineItemsEditor items={value[f.key] || []} onChange={(items, total) => set(f.key, items, null, null, total != null ? { selling_price: total } : {})} /></div>
            ) : f.type === "computed" ? (
              <Input data-testid={`field-${f.key}`} value={String(f.compute(value) ?? "")} readOnly disabled className="mt-1 bg-slate-100 text-slate-600" />
            ) : f.type === "select" ? (
              <Select value={value[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                <SelectTrigger data-testid={`field-${f.key}`} className="mt-1 bg-white"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : f.type === "lookup" ? (() => {
              const dep = f.dependsOn;
              const all = lookups[f.key] || [];
              const parentVal = dep ? value[dep.parent] : null;
              const opts = dep && parentVal ? all.filter((o) => o[dep.attr] === parentVal) : all;
              if (dep && parentVal && opts.length === 0) {
                return <div data-testid={`field-${f.key}-empty`} className="mt-1 text-xs text-slate-400 border border-dashed border-slate-200 rounded-md px-3 py-2.5">{dep.emptyText || "No options for this selection"}</div>;
              }
              return (
                <Select value={value[f.key] || ""} onValueChange={(v) => {
                  const opt = all.find((o) => o.id === v);
                  const extra = {};
                  if (dep && !parentVal && opt && opt[dep.attr]) {
                    extra[dep.parent] = opt[dep.attr];
                    if (dep.parentMirror) extra[dep.parentMirror] = opt[dep.parentMirrorAttr] || "";
                  }
                  set(f.key, v, f.mirror, opt ? opt[f.labelKey] : "", extra);
                }}>
                  <SelectTrigger data-testid={`field-${f.key}`} className="mt-1 bg-white"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {opts.map((o) => <SelectItem key={o.id} value={o.id}>{o[f.labelKey]}</SelectItem>)}
                  </SelectContent>
                </Select>
              );
            })() : (
              <Input data-testid={`field-${f.key}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                value={value[f.key] || ""} onChange={(e) => set(f.key, f.type === "number" ? e.target.value : e.target.value)} className="mt-1 bg-white" />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function cellValue(row, col, currency) {
  const v = row[col.key];
  if (col.tagJob && row.job_ref) {
    return (
      <span className="inline-flex items-center gap-1.5 flex-wrap">{v}
        <Link to={`/app/jobs?open=${row.job_id}`} onClick={(e) => e.stopPropagation()} data-testid={`quote-jobtag-${row.id}`}
          className="text-[11px] accent-text hover:underline bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 whitespace-nowrap">→ {row.job_ref}</Link>
      </span>
    );
  }
  if (col.type === "badge") return <StatusBadge status={v} testid={`badge-${row.id}`} />;
  if (col.type === "money") return money(v, currency);
  if (col.type === "date") return fmtDate(v);
  if (v === undefined || v === null || v === "") return <span className="text-slate-400">—</span>;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

export default function DataModule({ module, currency = "EUR", onNewClick, newLabel, reloadKey, children }) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [initialForm, setInitialForm] = useState("{}");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const { user } = useAuth();
  const isSuper = user?.role === "amden_super_admin";
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(() => {
    setRows(null);
    api.get(`/${module.entity}`, { params: q ? { q } : {} })
      .then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [module.entity, q]);

  useEffect(() => { load(); }, [load, reloadKey]);

  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && rows) {
      const row = rows.find((r) => r.id === openId);
      if (row) { setForm({ ...row }); setInitialForm(JSON.stringify({ ...row })); setEditing(row); setOpen(true); }
      else toast.error("That record no longer exists");
      setSearchParams({}, { replace: true });
    }
  }, [rows, searchParams, setSearchParams]);

  const openNew = () => {
    const defaults = {};
    module.fields.forEach((f) => { if (f.default !== undefined) defaults[f.key] = f.default; });
    setForm(defaults); setInitialForm(JSON.stringify(defaults)); setEditing(null); setOpen(true);
  };
  const openEdit = (row) => { setForm({ ...row }); setInitialForm(JSON.stringify({ ...row })); setEditing(row); setOpen(true); };

  const requestClose = () => {
    if (JSON.stringify(form) !== initialForm) setConfirmDiscard(true);
    else setOpen(false);
  };

  const save = async () => {
    for (const f of module.fields) if (f.required && !form[f.key]) { toast.error(`${f.label} is required`); return; }
    const payload = { ...form };
    module.fields.forEach((f) => {
      if (f.type === "computed") { const v = f.compute(form); payload[f.key] = v === "" ? null : v; }
    });
    setSaving(true);
    try {
      if (editing) await api.put(`/${module.entity}/${editing.id}`, payload);
      else await api.post(`/${module.entity}`, payload);
      toast.success(`${module.singular} saved`);
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const hardDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/admin/records/${module.entity}/${confirmDel.id}`);
      toast.success(`${module.singular} deleted permanently`);
      setConfirmDel(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-head font-bold text-slate-900">{module.label}</h1>
          <p className="text-sm text-slate-500">{rows ? `${rows.length} records` : "Loading…"}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input data-testid="module-search" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 w-56 bg-white" />
          </div>
          <Button data-testid="module-new-btn" className="btn-accent" onClick={onNewClick || openNew}>
            <Plus className="h-4 w-4 mr-1" /> {newLabel || `New ${module.singular}`}
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto scroll-thin">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 sticky top-0">
            <tr>{module.columns.map((c) => <th key={c.key} className={`text-left font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap ${c.w || ""}`}>{c.label}</th>)}{isSuper && <th className="w-10"></th>}</tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={module.columns.length + (isSuper ? 1 : 0)} className="px-3 py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {rows && rows.length === 0 && <tr><td colSpan={module.columns.length + (isSuper ? 1 : 0)} className="px-3 py-10 text-center text-slate-400">No records yet. Create your first {module.singular.toLowerCase()}.</td></tr>}
            {rows && rows.map((row) => (
              <tr key={row.id} data-testid={`row-${row.id}`}
                className={`data-row border-t border-slate-100 cursor-pointer ${module.entity === "stock" && isLowStock(row) ? "bg-amber-50" : ""}`}
                onClick={() => openEdit(row)}>
                {module.columns.map((c) => (
                  <td key={c.key} className={`px-3 py-2.5 text-slate-700 align-top ${c.wrap ? "whitespace-normal break-words" : "whitespace-nowrap"}`}>
                    {module.entity === "stock" && c.key === "quantity_available"
                      ? <StockQtyCell row={row} onChanged={(u) => setRows((rs) => rs.map((r) => (r.id === u.id ? u : r)))} />
                      : cellValue(row, c, currency)}
                  </td>
                ))}
                {isSuper && (
                  <td className="px-2 py-2.5">
                    <button data-testid={`hard-delete-${row.id}`} title="Delete permanently (Super Admin)"
                      onClick={(e) => { e.stopPropagation(); setConfirmDel(row); }}
                      className="text-slate-300 hover:text-red-600 transition-colors p-1">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) requestClose(); else setOpen(o); }}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2">
              {editing ? `Edit ${module.singular} · ${editing.ref || ""}` : `New ${module.singular}`}
              {module.qr && editing && <QrCode className="h-4 w-4 text-slate-400" />}
            </DialogTitle>
          </DialogHeader>
          {module.qr && editing && (
            <div className="flex items-center gap-3 bg-slate-50 rounded-md p-3 border border-slate-200">
              <img alt="qr" className="h-24 w-24" src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(editing.ref || editing.id)}`} />
              <div className="text-xs text-slate-500">Scan to view this asset, log a defect, or start an inspection.<br />Asset {editing.ref}</div>
            </div>
          )}
          <RecordForm fields={module.fields} value={form} onChange={setForm} />
          <DialogFooter>
            {module.entity === "contracts" && editing && (
              <Button variant="outline" data-testid="contract-to-template" className="mr-auto" onClick={async () => {
                try {
                  const { data } = await api.post(`/contracts/${editing.id}/create-template`);
                  toast.success(`Recurring template ${data.template.ref} created · ${data.created} visit(s) generated`);
                  setOpen(false);
                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }}>
                <Repeat className="h-4 w-4 mr-1" />Create recurring template
              </Button>
            )}
            {module.entity === "defects" && editing && (
              <Button variant="outline" data-testid="defect-to-quote" className="mr-auto" onClick={async () => {
                try {
                  const { data } = await api.post(`/defects/${editing.id}/create-quotation`);
                  toast.success(`Quotation ${data.quotation.ref} created from ${editing.ref}`);
                  setOpen(false);
                  navigate("/app/quotations");
                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }}>
                <FileText className="h-4 w-4 mr-1" />Create quotation
              </Button>
            )}
            {module.entity === "quotations" && editing && (editing.status === "Accepted" || form.status === "Accepted") && editing.status !== "Converted to Job" && (
              <Button variant="outline" data-testid="quote-to-job" className="mr-auto" onClick={async () => {
                try {
                  if (form.status === "Accepted" && editing.status !== "Accepted") await api.put(`/quotations/${editing.id}`, form);
                  const { data } = await api.post(`/quotations/${editing.id}/create-job`);
                  toast.success(`Job ${data.job.ref} created from ${editing.ref} — it's in Unscheduled, ready to book`);
                  setOpen(false);
                  navigate("/app/unscheduled");
                } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
              }}>
                <Briefcase className="h-4 w-4 mr-1" />Convert to Job
              </Button>
            )}
            {module.entity === "quotations" && editing?.status === "Converted to Job" && (
              <span className="mr-auto text-xs text-emerald-600 font-medium self-center" data-testid="quote-converted-label">Converted → {editing.job_id ? <Link to={`/app/jobs?open=${editing.job_id}`} className="underline hover:text-emerald-700">{editing.job_ref}</Link> : editing.job_ref}</span>
            )}
            {module.entity === "quotations" && editing?.source_defect_ref && (
              <Link to={`/app/defects?open=${editing.source_defect_id}`} data-testid="quote-defect-link" className="text-xs accent-text hover:underline self-center mr-2">From defect {editing.source_defect_ref}</Link>
            )}
            {module.entity === "quotations" && editing && (
              <Button variant="outline" data-testid="quote-pdf-btn" onClick={async () => {
                try {
                  const res = await api.get(`/quotations/${editing.id}/pdf`, { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url; a.download = `Quotation-${editing.ref}.pdf`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                } catch { toast.error("PDF download failed"); }
              }}>
                <Download className="h-4 w-4 mr-1" />Download PDF
              </Button>
            )}
            <Button variant="outline" data-testid="module-cancel-btn" onClick={requestClose}>Cancel</Button>
            <Button data-testid="module-save-btn" className="btn-accent" onClick={save} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete {confirmDel?.ref || confirmDel?.name || `this ${module.singular.toLowerCase()}`}?</AlertDialogTitle>
            <AlertDialogDescription>This hard-deletes the record for good — it cannot be undone. An audit trail entry will be kept.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-hard-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-hard-delete" className="bg-red-600 hover:bg-red-700 text-white" onClick={hardDelete}>Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>You have unsaved edits — closing will lose them.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="keep-editing-btn">Keep editing</AlertDialogCancel>
            <AlertDialogAction data-testid="discard-changes-btn" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => { setConfirmDiscard(false); setOpen(false); }}>Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {children}
    </div>
  );
}
